import { createAppError } from '../../api/errors';
import { commissionLedgerFixtures } from './fixtures';
import type { CommissionLedgerRepository } from './repository';
import type {
  CommissionAdjustmentInput,
  CommissionLedgerEntry,
  CommissionLedgerListQuery,
} from './types';
import {
  addExactSignedDecimals,
  bangkokWallTimeToRfc3339,
  compareExactSignedDecimals,
} from './types';

export interface MockCommissionLedgerRepositoryOptions {
  readonly empty?: boolean;
  readonly listError?: boolean;
}

// Stable, human-readable route identifiers resolve to the first fixture without duplicating
// fixture rows or altering list/pagination totals.
const commissionLedgerDetailAliases: Readonly<Record<string, string>> = {
  'entry-a': commissionLedgerFixtures[0]!.id,
};

export class MockCommissionLedgerRepository implements CommissionLedgerRepository {
  readonly #options: MockCommissionLedgerRepositoryOptions;
  readonly #entries: readonly CommissionLedgerEntry[];
  #approvalSequence = 0;

  constructor(options: MockCommissionLedgerRepositoryOptions = {}) {
    this.#options = options;
    this.#entries = options.empty === true ? [] : commissionLedgerFixtures;
  }

  async list(query: CommissionLedgerListQuery, signal: AbortSignal) {
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    if (this.#options.listError === true) {
      throw createAppError({
        status: 500,
        code: 'MOCK_LEDGER_LIST_FAILED',
        message: 'internal mock ledger details',
        requestId: 'mock-ledger-list-request',
        fieldErrors: {},
        retryAfterSeconds: null,
      });
    }
    const filtered = this.#entries
      .filter((entry) => matches(entry, query))
      .toSorted((left, right) => compareTimestamp(left, right, query.sort));
    const start = parseCursor(query.cursor);
    const items = filtered.slice(start, start + query.pageSize);
    const next = start + items.length;
    return {
      items,
      nextCursor: next < filtered.length ? `mock-cursor-${next}` : null,
      requestId: 'mock-ledger-list',
    };
  }

  async get(entryId: string, signal: AbortSignal) {
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    const resolvedId = commissionLedgerDetailAliases[entryId] ?? entryId;
    const entry = this.#entries.find((candidate) => candidate.id === resolvedId);
    if (entry === undefined) {
      throw createAppError({
        status: 404,
        code: 'LEDGER_ENTRY_NOT_FOUND',
        message: 'Commission ledger entry was not found',
        requestId: 'mock-ledger-detail-request',
        fieldErrors: {},
        retryAfterSeconds: null,
      });
    }
    return entry;
  }

  async previewMockAdjustment(input: CommissionAdjustmentInput) {
    const availableBefore = '100.000000';
    return {
      ...input,
      currencyCode: input.currencyCode.toUpperCase(),
      availableBefore,
      availableAfter: addExactSignedDecimals(availableBefore, input.signedAmount, 6),
    };
  }

  async submitMockAdjustment(_input: CommissionAdjustmentInput) {
    this.#approvalSequence += 1;
    return {
      approvalId: `MOCK-APPROVAL-${String(this.#approvalSequence).padStart(4, '0')}`,
      status: 'PENDING_MOCK_APPROVAL' as const,
      submittedAt: '2026-07-23T12:00:00+07:00',
    };
  }
}

function matches(entry: CommissionLedgerEntry, query: CommissionLedgerListQuery): boolean {
  const filters = query.filters;
  const from =
    filters.effectiveFrom.length === 0
      ? null
      : Date.parse(bangkokWallTimeToRfc3339(filters.effectiveFrom));
  const to =
    filters.effectiveTo.length === 0
      ? null
      : Date.parse(bangkokWallTimeToRfc3339(filters.effectiveTo));
  const effective = Date.parse(entry.effectiveAt);
  return (
    includes(entry.partner.id, filters.partnerId) &&
    includes(entry.agentId, filters.agentId) &&
    (filters.beneficiaryType === '' || entry.beneficiaryType === filters.beneficiaryType) &&
    includes(entry.beneficiaryId, filters.beneficiaryId) &&
    includes(entry.beneficiaryUsername, filters.beneficiaryUsername) &&
    includes(entry.source.transactionId, filters.transactionId) &&
    includes(entry.source.partnerTransactionId, filters.partnerTransactionId) &&
    (filters.entryType === '' || entry.entryType === filters.entryType) &&
    (filters.currency === '' || entry.currencyCode === filters.currency.toUpperCase()) &&
    (filters.minimumAmount === '' ||
      compareExactSignedDecimals(entry.signedAmount, filters.minimumAmount) >= 0) &&
    (filters.maximumAmount === '' ||
      compareExactSignedDecimals(entry.signedAmount, filters.maximumAmount) <= 0) &&
    (from === null || effective >= from) &&
    (to === null || effective < to)
  );
}

function includes(value: string | null, filter: string): boolean {
  return filter.length === 0 || (value ?? '').toLowerCase().includes(filter.toLowerCase());
}

function compareTimestamp(
  left: CommissionLedgerEntry,
  right: CommissionLedgerEntry,
  sort: CommissionLedgerListQuery['sort'],
): number {
  const primary = Date.parse(right[sort]) - Date.parse(left[sort]);
  return primary === 0 ? right.id.localeCompare(left.id) : primary;
}

function parseCursor(cursor: string): number {
  if (cursor.length === 0) return 0;
  const match = /^mock-cursor-(\d+)$/u.exec(cursor);
  return match === null ? 0 : Number(match[1]);
}
